import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  TextField,
  Grid,
  MenuItem,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box
} from '@mui/material';
import DialogContentText from '@mui/material/DialogContentText';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { pl } from 'date-fns/locale';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoIcon from '@mui/icons-material/Info';
import { format } from 'date-fns';
import { AuthContext } from '../../contexts/AuthContext';
import { getLogs } from '../../services/logs';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';

const History = () => {
  const { userRole } = useContext(AuthContext);
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedDetails, setSelectedDetails] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  const [filters, setFilters] = useState({
    action: '',
    entity_type: '',
    entity_id: '',
    user_id: '',
    location: '',
    from_date: null,
    to_date: null
  });

  // Funkcja do pobierania użytkowników - z useCallback
  const fetchUsers = useCallback(async () => {
    try {
      console.log('Pobieranie użytkowników...');
      const usersCollection = collection(db, 'users');
      const usersSnapshot = await getDocs(usersCollection);
      const usersMap = {};
      usersSnapshot.forEach((doc) => {
        const userData = doc.data();
        if (userData.first_name && userData.last_name) {
          usersMap[doc.id] = `${userData.first_name} ${userData.last_name}`;
        } else if (userData.username) {
          usersMap[doc.id] = userData.username;
        } else {
          usersMap[doc.id] = doc.id;
        }
        console.log('Dodano użytkownika:', doc.id, usersMap[doc.id]);
      });
      console.log('Mapa użytkowników:', usersMap);
      setUsers(usersMap);
    } catch (error) {
      console.error('Błąd podczas pobierania użytkowników:', error);
    }
  }, []);

  // Funkcja do pobierania logów
  const fetchLogs = async (shouldReset = false) => {
    if (loading) return;
    setLoading(true);
    console.log('fetchLogs start'); // Dodane do debugowania
    
    try {
      if (shouldReset) {
        setPage(0);
        setLastDoc(null);
      }

      const result = await getLogs(filters, rowsPerPage, shouldReset ? null : lastDoc);

      if (shouldReset) {
        setLogs(result.logs);
      } else {
        setLogs(prev => [...prev, ...result.logs]);
      }

      setLastDoc(result.lastDoc);
      setHasMore(result.logs.length === rowsPerPage);
    } catch (error) {
      console.error('Error fetching logs:', error);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  // Efekt do inicjalizacji przy pierwszym renderowaniu
  // Pobierz użytkowników przy pierwszym renderowaniu
  useEffect(() => {
    console.log('Pierwsze renderowanie - pobieranie użytkowników...');
    fetchUsers();
  }, []);

  // Pobierz logi przy zmianie filtrów
  useEffect(() => {
    console.log('Zmiana filtrów - pobieranie logów...');
    fetchLogs();
  }, [filters]);

  // Mapowanie typów akcji na przyjazne nazwy
  const actionTypes = {
    // Użytkownicy
    user_create: 'Utworzenie użytkownika',
    user_update: 'Aktualizacja użytkownika',
    user_delete: 'Usunięcie użytkownika',
    user_login: 'Logowanie użytkownika',
    user_logout: 'Wylogowanie użytkownika',

    // Produkty
    product_create: 'Dodanie produktu',
    product_update: 'Aktualizacja produktu',
    product_delete: 'Usunięcie produktu',
    product_image_add: 'Dodanie zdjęcia produktu',
    product_image_delete: 'Usunięcie zdjęcia produktu',

    // Transfery
    transfer_create: 'Nowy transfer',
    transfer_update: 'Aktualizacja transferu',
    transfer_complete: 'Zakończenie transferu',
    transfer_cancel: 'Anulowanie transferu',

    // Zamówienia
    order_create: 'Nowe zamówienie',
    order_update: 'Aktualizacja zamówienia',
    order_status_change: 'Zmiana statusu zamówienia',

    // Warsztat
    workshop_item_create: 'Nowa naprawa',
    workshop_item_update: 'Aktualizacja naprawy',
    workshop_item_status_change: 'Zmiana statusu naprawy',

    // Grafik
    schedule_create: 'Dodanie zmiany',
    schedule_update: 'Aktualizacja zmiany',
    schedule_delete: 'Usunięcie zmiany',
    schedule_approve: 'Zatwierdzenie zmiany',
    schedule_reject: 'Odrzucenie zmiany',
    schedule_approve_all: 'Zatwierdzenie wszystkich zmian',
    schedule_print: 'Wydruk grafiku',
    payroll_print: 'Wydruk listy płac',
    schedule_delete: 'Usunięcie zmiany',

    // CRM
    client_create: 'Dodanie klienta',
    client_update: 'Aktualizacja klienta',
    client_delete: 'Usunięcie klienta',

    // Magazyn
    inventory_update: 'Aktualizacja stanu magazynowego'
  };

  // Mapowanie typów encji na przyjazne nazwy
  const entityTypes = {
    user: 'Użytkownik',
    product: 'Produkt',
    transfer: 'Transfer',
    order: 'Zamówienie',
    workshop_item: 'Naprawa',
    schedule: 'Grafik',
    client: 'Klient',
    inventory: 'Magazyn'
  };

  // Style dla różnych typów akcji
  const actionStyles = {
    // Produkty
    product_create: { color: '#4caf50', icon: '➕' },  // zielony
    product_update: { color: '#2196f3', icon: '✏️' },  // niebieski
    product_delete: { color: '#f44336', icon: '🗑️' },  // czerwony
    product_image_add: { color: '#4caf50', icon: '🖼️' },
    product_image_delete: { color: '#f44336', icon: '🖼️' },

    // Transfery
    transfer_create: { color: '#4caf50', icon: '📦' },
    transfer_update: { color: '#2196f3', icon: '📦' },
    transfer_complete: { color: '#4caf50', icon: '✅' },
    transfer_cancel: { color: '#f44336', icon: '❌' },

    // Zamówienia
    order_create: { color: '#4caf50', icon: '🛍️' },
    order_update: { color: '#2196f3', icon: '🛍️' },
    order_status_change: { color: '#ff9800', icon: '🔄' },  // pomarańczowy

    // Warsztat
    workshop_item_create: { color: '#4caf50', icon: '🔧' },
    workshop_item_update: { color: '#2196f3', icon: '🔧' },
    workshop_item_status_change: { color: '#ff9800', icon: '🔧' },

    // Grafik
    schedule_create: { color: '#4caf50', icon: '📅' },
    schedule_update: { color: '#2196f3', icon: '📅' },
    schedule_delete: { color: '#f44336', icon: '📅' },

    // CRM
    client_create: { color: '#4caf50', icon: '👤' },
    client_update: { color: '#2196f3', icon: '👤' },
    client_delete: { color: '#f44336', icon: '👤' },

    // Magazyn
    inventory_update: { color: '#ff9800', icon: '📊' }
  };

  // Mapowanie nazw pól na przyjazne nazwy
  const fieldNames = {
    sku: 'SKU',
    brand: 'Marka',
    model: 'Model',
    price: 'Cena',
    status: 'Status',
    category: 'Kategoria',
    location: 'Lokalizacja',
    material: 'Materiał',
    movement: 'Mechanizm',
    condition: 'Stan',
    ring_size: 'Rozmiar pierścionka',
    clasp_type: 'Typ zapięcia',
    collection: 'Kolekcja',
    created_at: 'Data utworzenia',
    dial_color: 'Kolor tarczy',
    updated_at: 'Data aktualizacji',
    description: 'Opis',
    gold_purity: 'Próba złota',
    stone_count: 'Liczba kamieni',
    stone_shape: 'Kształt kamienia',
    weight_grams: 'Waga (g)',
    bracelet_type: 'Typ bransolety',
    case_diameter: 'Średnica koperty',
    has_documents: 'Dokumenty',
    serial_number: 'Numer seryjny',
    colored_stones: 'Kamienie kolorowe',
    production_year: 'Rok produkcji',
    diamonds_setting: 'Oprawa diamentów',
    reference_number: 'Numer referencyjny',
    center_stone_size: 'Rozmiar głównego kamienia',
    total_carat_weight: 'Łączna waga karatów',
    certificate_numbers: 'Numery certyfikatów'
  };

  // Mapowanie wartości na przyjazne nazwy
  const valueMap = {
    // Status
    available: 'Dostępny',
    sold: 'Sprzedany',
    reserved: 'Zarezerwowany',
    
    // Kategorie
    watch: 'Zegarek',
    jewelry: 'Biżuteria',
    accessory: 'Akcesoria',
    
    // Stan
    new: 'Nowy',
    very_good: 'Bardzo dobry',
    good: 'Dobry',
    fair: 'Zadowalający',
    
    // Materiał
    steel: 'Stal',
    steel_yg: 'Stal/Złoto żółte',
    steel_rg: 'Stal/Złoto różowe',
    yg: 'Złoto żółte',
    wg: 'Złoto białe',
    rg: 'Złoto różowe',
    platinum: 'Platyna',
    
    // Oprawa diamentów
    none: 'Brak',
    pave: 'Pavé',
    channel: 'Kanałowa',
    bezel: 'Bezel',
    prong: 'Na sztyftach'
  };

  // Funkcja formatująca wartość
  const formatValue = (value, key) => {
    if (value === null || value === undefined) return '-';
    
    // Formatowanie daty
    if (key.includes('date') || key.includes('timestamp')) {
      try {
        return format(new Date(value), 'dd.MM.yyyy HH:mm:ss');
      } catch {
        return value;
      }
    }

    // Formatowanie ceny
    if (key.includes('price')) {
      try {
        return new Intl.NumberFormat('pl-PL', {
          style: 'currency',
          currency: 'PLN'
        }).format(value);
      } catch {
        return value;
      }
    }

    // Formatowanie boolean
    if (typeof value === 'boolean') {
      return value ? 'Tak' : 'Nie';
    }

    // Formatowanie obiektów i tablic
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }

    return value;
  };

  // Funkcja formatująca szczegóły logu
  const formatLogDetails = (details) => {
    if (!details) return null;

    // Mapowanie ról na polski
    const roleMap = {
      'UserRole.ADMIN': 'Administrator',
      'UserRole.EMPLOYEE': 'Pracownik',
      'UserRole.MANAGER': 'Kierownik'
    };

    // Mapowanie statusów na polski
    const statusMap = {
      'approved': 'Zatwierdzony',
      'pending': 'Oczekujący',
      'rejected': 'Odrzucony',
      'available': 'Dostępny',
      'sold': 'Sprzedany',
      'reserved': 'Zarezerwowany'
    };

    // Mapowanie kategorii na polski
    const categoryMap = {
      'watch': 'Zegarek',
      'jewelry': 'Biżuteria',
      'accessory': 'Akcesoria',
      'ProductCategory.WATCH': 'Zegarek',
      'ProductCategory.JEWELRY': 'Biżuteria',
      'ProductCategory.ACCESSORY': 'Akcesoria'
    };

    // Mapowanie materiałów na polski
    const materialMap = {
      'steel': 'Stal',
      'steel_yg': 'Stal/Złoto żółte',
      'steel_rg': 'Stal/Złoto różowe',
      'yg': 'Złoto żółte',
      'wg': 'Złoto białe',
      'rg': 'Złoto różowe',
      'platinum': 'Platyna'
    };

    // Mapowanie stanu na polski
    const conditionMap = {
      'new': 'Nowy',
      'very_good': 'Bardzo dobry',
      'good': 'Dobry',
      'fair': 'Zadowalający'
    };

    // Funkcja pomocnicza do formatowania daty
    const formatDateTime = (dateStr) => {
      if (!dateStr) return '-';
      const date = new Date(dateStr);
      return format(date, 'dd.MM.yyyy HH:mm');
    };

    // Formatowanie wartości w zależności od typu pola
    const formatValue = (value, key) => {
      if (value === null || value === undefined) return '-';
      
      switch(key) {
        case 'role':
          return roleMap[value] || value;
        case 'status':
          return statusMap[value] || value;
        case 'category':
          return categoryMap[value] || value;
        case 'material':
          return materialMap[value] || value;
        case 'condition':
          return conditionMap[value] || value;
        case 'price':
          return new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(value);
        case 'created_at':
        case 'updated_at':
        case 'approved_at':
          return formatDateTime(value);
        case 'has_documents':
          return value ? 'Tak' : 'Nie';
        case 'images':
          return Array.isArray(value) ? `${value.length} zdjęć` : value;
        case 'user_id':
        case 'approved_by_id':
          const user = users.find(u => u.id === Number(value));
          return user ? user.username : value;
        default:
          return value;
      }
    };

    // Jeśli mamy dane użytkownika
    if (details.username && details.email) {
      return (
        <Box sx={{ p: 1 }}>
          <Typography variant="h6" gutterBottom color="primary">
            Dane użytkownika:
          </Typography>
          <Box sx={{ pl: 2 }}>
            <Typography><strong>Nazwa użytkownika:</strong> {details.username}</Typography>
            <Typography><strong>Email:</strong> {details.email}</Typography>
            <Typography><strong>Rola:</strong> {formatValue(details.role, 'role')}</Typography>
          </Box>
        </Box>
      );
    }

    // Jeśli mamy nowo utworzony produkt
    if (details.created_data) {
      const product = details.created_data;
      return (
        <Box sx={{ p: 1 }}>
          <Typography variant="h6" gutterBottom color="success.main">
            Nowy produkt:
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="primary">Podstawowe informacje</Typography>
                <Box sx={{ pl: 2 }}>
                  <Typography><strong>Marka:</strong> {product.brand}</Typography>
                  <Typography><strong>Model:</strong> {product.model}</Typography>
                  <Typography><strong>Cena:</strong> {formatValue(product.price, 'price')}</Typography>
                  <Typography><strong>Kategoria:</strong> {formatValue(product.category, 'category')}</Typography>
                  <Typography><strong>Lokalizacja:</strong> {product.location}</Typography>
                  <Typography><strong>Status:</strong> {formatValue(product.status, 'status')}</Typography>
                </Box>
              </Box>
              {product.serial_number && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="primary">Numery</Typography>
                  <Box sx={{ pl: 2 }}>
                    <Typography><strong>Numer seryjny:</strong> {product.serial_number}</Typography>
                    {product.reference_number && (
                      <Typography><strong>Numer referencyjny:</strong> {product.reference_number}</Typography>
                    )}
                  </Box>
                </Box>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="primary">Szczegóły techniczne</Typography>
                <Box sx={{ pl: 2 }}>
                  {product.material && (
                    <Typography><strong>Materiał:</strong> {formatValue(product.material, 'material')}</Typography>
                  )}
                  {product.case_diameter && (
                    <Typography><strong>Średnica koperty:</strong> {product.case_diameter} mm</Typography>
                  )}
                  {product.bracelet_type && (
                    <Typography><strong>Typ bransolety:</strong> {product.bracelet_type}</Typography>
                  )}
                  {product.dial_color && (
                    <Typography><strong>Kolor tarczy:</strong> {product.dial_color}</Typography>
                  )}
                  <Typography><strong>Stan:</strong> {formatValue(product.condition, 'condition')}</Typography>
                  <Typography><strong>Dokumenty:</strong> {formatValue(product.has_documents, 'has_documents')}</Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      );
    }

    // Jeśli mamy wpis w grafiku
    if (details.date && details.start_time) {
      return (
        <Box sx={{ p: 1 }}>
          <Typography variant="h6" gutterBottom color="primary">
            Wpis w grafiku:
          </Typography>
          <Box sx={{ pl: 2 }}>
            <Typography><strong>Data:</strong> {format(new Date(details.date), 'dd.MM.yyyy')}</Typography>
            <Typography><strong>Godziny pracy:</strong> {details.start_time} - {details.end_time}</Typography>
            <Typography><strong>Lokalizacja:</strong> {details.location}</Typography>
            <Typography><strong>Pracownik:</strong> {formatValue(details.user_id, 'user_id')}</Typography>
            <Typography><strong>Status:</strong> {formatValue(details.status, 'status')}</Typography>
            <Typography><strong>Data zatwierdzenia:</strong> {formatValue(details.approved_at, 'approved_at')}</Typography>
            <Typography><strong>Zatwierdzone przez:</strong> {formatValue(details.approved_by_id, 'approved_by_id')}</Typography>
          </Box>
        </Box>
      );
    }

    // Jeśli mamy usunięty produkt
    if (details.deleted_data) {
      const product = details.deleted_data;
      return (
        <Box sx={{ p: 1 }}>
          <Typography variant="h6" gutterBottom color="error">
            Usunięty produkt:
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="primary">Podstawowe informacje</Typography>
                <Box sx={{ pl: 2 }}>
                  <Typography><strong>Marka:</strong> {product.brand}</Typography>
                  <Typography><strong>Model:</strong> {product.model}</Typography>
                  <Typography><strong>Cena:</strong> {formatValue(product.price, 'price')}</Typography>
                  <Typography><strong>Kategoria:</strong> {formatValue(product.category, 'category')}</Typography>
                  <Typography><strong>Lokalizacja:</strong> {product.location}</Typography>
                  <Typography><strong>Status:</strong> {formatValue(product.status, 'status')}</Typography>
                </Box>
              </Box>
              {product.serial_number && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="primary">Numery</Typography>
                  <Box sx={{ pl: 2 }}>
                    <Typography><strong>Numer seryjny:</strong> {product.serial_number}</Typography>
                    {product.reference_number && (
                      <Typography><strong>Numer referencyjny:</strong> {product.reference_number}</Typography>
                    )}
                  </Box>
                </Box>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="primary">Szczegóły techniczne</Typography>
                <Box sx={{ pl: 2 }}>
                  {product.material && (
                    <Typography><strong>Materiał:</strong> {formatValue(product.material, 'material')}</Typography>
                  )}
                  {product.case_diameter && (
                    <Typography><strong>Średnica koperty:</strong> {product.case_diameter} mm</Typography>
                  )}
                  {product.bracelet_type && (
                    <Typography><strong>Typ bransolety:</strong> {product.bracelet_type}</Typography>
                  )}
                  {product.dial_color && (
                    <Typography><strong>Kolor tarczy:</strong> {product.dial_color}</Typography>
                  )}
                  <Typography><strong>Stan:</strong> {formatValue(product.condition, 'condition')}</Typography>
                  <Typography><strong>Dokumenty:</strong> {formatValue(product.has_documents, 'has_documents')}</Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      );
    }

    // Jeśli mamy stare i nowe wartości (np. zmiana w grafiku)
    if (details.old && details.new) {
      const changes = [];
      const allKeys = [...new Set([...Object.keys(details.old), ...Object.keys(details.new)])];
      
      allKeys.forEach(key => {
        const oldVal = details.old[key];
        const newVal = details.new[key];
        
        if (oldVal !== newVal) {
          let label = '';
          let oldFormatted = oldVal;
          let newFormatted = newVal;

          // Mapowanie nazw pól na polski
          switch(key) {
            case 'date':
              label = 'Data zmiany';
              oldFormatted = format(new Date(oldVal), 'dd.MM.yyyy');
              newFormatted = format(new Date(newVal), 'dd.MM.yyyy');
              break;
            case 'status':
              label = 'Status';
              oldFormatted = statusMap[oldVal] || oldVal;
              newFormatted = statusMap[newVal] || newVal;
              break;
            case 'end_time':
              label = 'Godzina zakończenia';
              break;
            case 'start_time':
              label = 'Godzina rozpoczęcia';
              break;
            case 'location':
              label = 'Lokalizacja';
              break;
            case 'approved_at':
              label = 'Data zatwierdzenia';
              oldFormatted = formatDateTime(oldVal);
              newFormatted = formatDateTime(newVal);
              break;
            case 'approved_by_id':
              label = 'Zatwierdzone przez';
              oldFormatted = formatValue(oldVal, 'approved_by_id');
              newFormatted = formatValue(newVal, 'approved_by_id');
              break;
            case 'user_id':
              label = 'Pracownik';
              oldFormatted = formatValue(oldVal, 'user_id');
              newFormatted = formatValue(newVal, 'user_id');
              break;
            default:
              label = key;
          }

          changes.push(
            <Box key={key} sx={{ mb: 1.5 }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                {label}
              </Typography>
              <Box sx={{ pl: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography 
                  variant="body1" 
                  color="error" 
                  component="span" 
                  sx={{ textDecoration: 'line-through' }}
                >
                  {oldFormatted}
                </Typography>
                <Typography 
                  variant="body1"
                  color="success.main" 
                  component="span"
                  sx={{ fontWeight: 500 }}
                >
                  {newFormatted}
                </Typography>
              </Box>
            </Box>
          );
        }
      });

      return (
        <Box sx={{ p: 1 }}>
          <Typography variant="h6" gutterBottom color="primary">
            Wprowadzone zmiany:
          </Typography>
          {changes.length > 0 ? changes : (
            <Typography color="text.secondary">
              Brak zmian w wartościach
            </Typography>
          )}
        </Box>
      );
    }

    // Dla innych typów logów pokazujemy surowe dane
    return (
      <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
        {JSON.stringify(details, null, 2)}
      </pre>
    );
  };

  // Funkcja formatująca akcję
  const formatAction = (action) => {
    const style = actionStyles[action] || { color: '#757575', icon: '❔' };  // domyślny szary
    const actionName = actionTypes[action] || action;

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ fontSize: '1.2em' }}>{style.icon}</span>
        <Typography
          component="span"
          style={{ 
            color: style.color,
            fontWeight: 500
          }}
        >
          {actionName}
        </Typography>
      </div>
    );
  };

  // Funkcja formatująca szczegóły produktu
  const formatProductDetails = (details) => {
    const formatted = {};
    
    // Sortujemy pola według priorytetu
    const priorityFields = [
      'sku', 'brand', 'model', 'category', 'price', 'status', 'location',
      'material', 'condition', 'production_year', 'case_diameter', 'dial_color',
      'bracelet_type', 'movement', 'serial_number', 'reference_number',
      'has_documents', 'created_at', 'updated_at'
    ];
    
    // Najpierw dodajemy pola priorytetowe
    for (const key of priorityFields) {
      const value = formatValue(details[key], key);
      if (value !== null) {
        formatted[key] = value;
      }
    }
    
    // Następnie dodajemy pozostałe pola
    for (const [key, value] of Object.entries(details)) {
      if (!priorityFields.includes(key)) {
        const formattedValue = formatValue(value, key);
        if (formattedValue !== null) {
          formatted[key] = formattedValue;
        }
      }
    }
    
    return formatted;
  };


  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleRefresh = () => {
    setHasMore(true);
    setPage(0);
    fetchUsers();
    fetchLogs(true);
  };

  const handleCloseDetails = () => {
    setSelectedLog(null);
    setDetailsOpen(false);
  };

  const handleOpenDetails = (log) => {
    setSelectedLog(log);
    setDetailsOpen(true);
  };

  const formatDetails = (details) => {
    if (typeof details === 'object') {
      // Jeśli details jest już obiektem, nie musimy parsować
      const formattedEntries = Object.entries(details)
        .filter(([_, value]) => value !== undefined && value !== null)
        .map(([key, value]) => {
          // Jeśli wartość jest obiektem, sformatuj go
          if (typeof value === 'object' && value !== null) {
            return `${key}:\n${Object.entries(value)
              .map(([subKey, subValue]) => `  ${subKey}: ${subValue}`)
              .join('\n')}`;
          }
          return `${key}: ${value}`;
        });
      return formattedEntries.join('\n');
    }
    
    try {
      // Jeśli details jest stringiem, spróbuj sparsować
      const parsed = JSON.parse(details);
      return formatDetails(parsed); // Rekurencyjnie formatuj sparsowany obiekt
    } catch (e) {
      // Jeśli nie da się sparsować, zwróć oryginalny string
      return String(details);
    }
  };

  const handleDetailsClick = (details) => {
    setSelectedDetails(formatDetails(details));
    setDetailsDialogOpen(true);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    if (newPage > page && hasMore) {
      fetchLogs(false);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    const newValue = parseInt(event.target.value, 10);
    setRowsPerPage(newValue);
  };

  if (userRole !== 'admin') {
    return (
      <Container>
        <Typography variant="h6" color="error" sx={{ mt: 4 }}>
          Brak dostępu. Ta strona jest dostępna tylko dla administratorów.
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3}>
        {/* Filtry */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Filtry
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    select
                    fullWidth
                    label="Akcja"
                    value={filters.action}
                    onChange={(e) => handleFilterChange('action', e.target.value)}
                  >
                    <MenuItem value="">Wszystkie</MenuItem>
                    {Object.entries(actionTypes).map(([value, label]) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    select
                    fullWidth
                    label="Typ encji"
                    value={filters.entity_type}
                    onChange={(e) => handleFilterChange('entity_type', e.target.value)}
                  >
                    <MenuItem value="">Wszystkie</MenuItem>
                    {Object.entries(entityTypes).map(([value, label]) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="ID encji"
                    value={filters.entity_id}
                    onChange={(e) => handleFilterChange('entity_id', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    select
                    fullWidth
                    label="Użytkownik"
                    value={filters.user_id}
                    onChange={(e) => handleFilterChange('user_id', e.target.value)}
                  >
                    <MenuItem value="">Wszyscy</MenuItem>
                    {Object.entries(users).map(([id, name]) => (
                      <MenuItem key={id} value={id}>
                        {name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={pl}>
                    <DateTimePicker
                      label="Od daty"
                      value={filters.from_date}
                      onChange={(date) => handleFilterChange('from_date', date)}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                      ampm={false}
                      format="dd.MM.yyyy HH:mm"
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={pl}>
                    <DateTimePicker
                      label="Do daty"
                      value={filters.to_date}
                      onChange={(date) => handleFilterChange('to_date', date)}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                      ampm={false}
                      format="dd.MM.yyyy HH:mm"
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleRefresh}
                    startIcon={<RefreshIcon />}
                    sx={{ height: '56px' }}
                    fullWidth
                    disabled={loading}
                  >
                    {loading ? 'Ładowanie...' : 'Odśwież'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Grid>

        {/* Lista logów */}
        <Grid item xs={12}>
          <TableContainer component={Paper}>
            <Table>
              <TableBody>
                {loading && logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body1" color="textSecondary">
                        Brak logów do wyświetlenia
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((log, index) => (
                    <TableRow 
                      key={`${log.id}-${log.timestamp}-${index}`}
                      hover
                      sx={{
                        '&:last-child td, &:last-child th': { border: 0 },
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)'
                        }
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {format(new Date(log.timestamp), 'dd.MM.yyyy')}
                        </Typography>
                        <Typography variant="body2" style={{ fontSize: '0.8em' }}>
                          {format(new Date(log.timestamp), 'HH:mm:ss')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {formatAction(log.action)}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          style={{
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            display: 'inline-block',
                            color: '#fff'
                          }}
                        >
                          {entityTypes[log.entity_type] || log.entity_type}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          #{log.entity_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          <span style={{ fontSize: '1.1em' }}>👤</span>
                          {users[log.user_id] || log.user_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Szczegóły">
                          <IconButton
                            size="small"
                            onClick={() => handleDetailsClick(log.details)}
                          >
                            <InfoIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={-1}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Wierszy na stronie:"
              labelDisplayedRows={({ from, to }) => `${from}–${to}`}
            />
          </TableContainer>
        </Grid>

        {/* Dialog ze szczegółami */}
        <Dialog open={detailsDialogOpen} onClose={() => setDetailsDialogOpen(false)}>
          <DialogTitle>Szczegóły</DialogTitle>
          <DialogContent>
            <DialogContentText style={{ whiteSpace: 'pre-line', fontFamily: 'monospace' }}>
              {selectedDetails}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailsDialogOpen(false)} variant="contained">Zamknij</Button>
          </DialogActions>
        </Dialog>
      </Grid>
    </Container>
  );
};

export default History;
